// src/pages/Login.jsx
import React, { useState } from 'react';
import axios from 'axios';
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Divider,
  Link
} from '@mui/material';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { backendUrl } from "../config.js";
import { CircularProgress } from '@mui/material';

// Client-side password validation
const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return null;
};

const Login = () => {
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleModeChange = (newMode) => {
    setMode(newMode);
    // Reset form data when switching mode
    setFormData({ name: '', email: '', password: '' });
  };

  // Update state as user types
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission for login and signup differently
  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (mode === 'signup') {
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        newErrors.password = passwordError;
      }
    } else if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    // Name validation for signup
    if (mode === 'signup' && !formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Show the first error in snackbar
      const firstError = Object.values(errors)[0];
      if (firstError) {
        enqueueSnackbar(firstError, { variant: 'error' });
      }
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (mode === 'login') {
        // Login: Send email and password
        const response = await axios.post(
          `${backendUrl}/api/users/login`,
          {
            email: formData.email,
            password: formData.password,
          }
        );
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          window.dispatchEvent(new Event('authChange'));
          enqueueSnackbar('Login successful!', { variant: 'success' });
          navigate('/');
        } else {
          enqueueSnackbar(response.data.message, { variant: 'error' });
        }
      } else {
        // Signup: Send name, email, and password
        const response = await axios.post(
          `${backendUrl}/api/users/register`,
          {
            name: formData.name,
            email: formData.email,
            password: formData.password,
          }
        );
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          window.dispatchEvent(new Event('authChange'));
          enqueueSnackbar('Registration successful!', { variant: 'success' });
          navigate('/');
        } else {
          enqueueSnackbar(response.data.message, { variant: 'error' });
        }
      }
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.';
      
      if (error.response) {
        // Handle server-side validation errors
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid request. Please check your input.';
        } else if (error.response.status === 401) {
          errorMessage = 'Invalid email or password';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      enqueueSnackbar(errorMessage, { variant: 'error' });
      console.error('Error during authentication:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card>
        <CardContent>
          {/* Title */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              {mode === 'login' ? 'Login' : 'Sign Up'}
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <TextField
                label="Name"
                variant="outlined"
                fullWidth
                required
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              label="Email"
              variant="outlined"
              type="email"
              fullWidth
              required
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              fullWidth
              required
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            {mode === 'login' && (
              <Typography variant="body2" align="right" sx={{ mb: 2 }}>
                <NavLink
                  to="/forgot-password"
                  style={{ textDecoration: 'none', color: '#1976d2' }}
                >
                  Forgot Password?
                </NavLink>
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ mt: 2, mb: 2 }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : mode === 'login' ? 'Sign In' : 'Sign Up'}
            </Button>
          </form>

          <Typography variant="body2" align="center">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <Link
              component="button"
              variant="body2"
              onClick={() => handleModeChange(mode === 'login' ? 'signup' : 'login')}
              sx={{ fontWeight: 'bold' }}
            >
              {mode === 'login' ? 'Sign Up' : 'Login'}
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Login;
